/**
 * Calendar deep links (fix-list F1).
 *
 * `/calendar?event=<id>` opens that event's details: the chat event card
 * (components/messages/EventCard.vue), the topbar bell and the
 * notifications page link this way. Older notification links carry
 * `?id=<id>`; the calendar still opens them and rewrites the URL to
 * `?event=`. While an event is open the URL keeps `?event=`, so a refresh
 * reopens it (the tab-persistence pattern).
 */
import type { LocationQuery, LocationQueryRaw } from 'vue-router';

export const EVENT_QUERY = 'event';
/** Older notification links: `/calendar?id=<eventId>`. */
const LEGACY_EVENT_QUERY = 'id';

const first = (v: unknown): unknown => (Array.isArray(v) ? v[0] : v);

function positiveInt(value: unknown): number | null {
  const v = first(value);
  const n = typeof v === 'number' ? v : typeof v === 'string' && /^\d+$/.test(v) ? Number(v) : NaN;
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/** The calendar path that opens one event, or the calendar itself for a missing / invalid id. */
export function calendarEventPath(eventId: unknown): string {
  const id = Array.isArray(eventId) ? null : positiveInt(eventId);
  return id === null ? '/calendar' : `/calendar?${EVENT_QUERY}=${id}`;
}

/** The event the calendar URL points at: `?event=`, else the legacy `?id=`; null when neither is a valid id. */
export function linkedEventId(query: LocationQuery): number | null {
  return positiveInt(query[EVENT_QUERY]) ?? positiveInt(query[LEGACY_EVENT_QUERY]);
}

/** Whether the URL carries an event link at all (valid or not). */
export function hasEventLink(query: LocationQuery): boolean {
  return query[EVENT_QUERY] !== undefined || query[LEGACY_EVENT_QUERY] !== undefined;
}

/** A copy of `query` that points at `eventId` (or at no event), without the legacy `id`. */
export function withLinkedEvent(query: LocationQuery, eventId: number | null): LocationQueryRaw {
  const rest: LocationQueryRaw = { ...query };
  delete rest[EVENT_QUERY];
  delete rest[LEGACY_EVENT_QUERY];
  return eventId === null ? rest : { ...rest, [EVENT_QUERY]: String(eventId) };
}

/**
 * What a failed GET /events/:id means for a link: 'noAccess' when the event
 * is outside the caller's calendar scope or gone (404 — never a 403, so no
 * existence probe — or a 403), else a transient 'error'.
 */
export function linkFailure(err: unknown): 'noAccess' | 'error' {
  const status = (err as { response?: { status?: unknown } } | null | undefined)?.response?.status;
  return status === 404 || status === 403 ? 'noAccess' : 'error';
}
